// src/pages/finance-portal/reports/BalanceSheet.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack, Chip,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatPercent,
    todayIso, downloadCsv, printReport, AccountLink,
} from './reportShared';

export default function BalanceSheet() {
    const [asOf, setAsOf] = useState(todayIso());
    const [format, setFormat] = useState('CLASSIFIED');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/balance-sheet', {
                params: { as_of: asOf, format },
            });
            if (res.data && res.data.success) {
                setData({
                    ...res.data,
                    assets: Array.isArray(res.data.assets) ? res.data.assets : [],
                    liabilities: Array.isArray(res.data.liabilities) ? res.data.liabilities : [],
                    equity: Array.isArray(res.data.equity) ? res.data.equity : [],
                });
            } else {
                setError(res.data?.message || 'Failed to load');
            }
        } catch (e) {
            console.error('Balance Sheet load error:', e);
            setError(e.response?.data?.message || e.message || 'Failed to load balance sheet');
        } finally {
            setLoading(false);
        }
    }, [asOf, format]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [
            { section: 'Assets' },
            ...data.assets.map((r) => ({
                section: '', account: `${r.account_number} ${r.account_name}`, amount: r.balance,
            })),
            { section: 'Total Assets', amount: data.total_assets },
            { section: 'Liabilities' },
            ...data.liabilities.map((r) => ({
                section: '', account: `${r.account_number} ${r.account_name}`, amount: r.balance,
            })),
            { section: 'Total Liabilities', amount: data.total_liabilities },
            { section: 'Equity' },
            ...data.equity.map((r) => ({
                section: '', account: `${r.account_number} ${r.account_name}`, amount: r.balance,
            })),
            { section: 'Net Income (YTD)', amount: data.current_period_net_income },
            { section: 'Total Equity', amount: data.total_equity },
            { section: 'Total Liabilities + Equity', amount: data.total_liabilities_and_equity },
        ];
        downloadCsv(`balance_sheet_${asOf}.csv`, rows, ['section', 'account', 'amount']);
    };

    const renderRows = (rows) =>
        rows.map((r) => (
            <TableRow key={r.account_id}>
                <TableCell>
                    <AccountLink
                        accountId={r.account_id}
                        label={`${r.account_number} — ${r.account_name}`}
                        asOf={asOf}
                    />
                </TableCell>
                <TableCell align="right">{formatCurrency(r.balance)}</TableCell>
                {format === 'COMMON_SIZE' && (
                    <TableCell align="right">{formatPercent(r.percent_of_assets || 0)}</TableCell>
                )}
            </TableRow>
        ));

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <AccountBalanceIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Balance Sheet</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField label="As of" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Format</InputLabel>
                            <Select label="Format" value={format} onChange={(e) => setFormat(e.target.value)}>
                                <MenuItem value="CLASSIFIED">Classified</MenuItem>
                                <MenuItem value="STANDARD">Standard</MenuItem>
                                <MenuItem value="COMMON_SIZE">Common-size</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 3 }} id="bs-report">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Balance Sheet</Typography>
                            <Typography variant="body2" color="text.secondary">As of {data.as_of}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<PrintIcon />} onClick={() => printReport('bs-report')}>Print</Button>
                            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</Button>
                        </Stack>
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    {format === 'COMMON_SIZE' && <TableCell align="right">% of Assets</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow><TableCell colSpan={3} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>ASSETS</TableCell></TableRow>
                                {renderRows(data.assets || [])}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Total Assets</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_assets)}</TableCell>
                                    {format === 'COMMON_SIZE' && <TableCell align="right">100.00%</TableCell>}
                                </TableRow>

                                <TableRow><TableCell colSpan={3} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>LIABILITIES</TableCell></TableRow>
                                {renderRows(data.liabilities || [])}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Total Liabilities</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_liabilities)}</TableCell>
                                    {format === 'COMMON_SIZE' && <TableCell align="right" />}
                                </TableRow>

                                <TableRow><TableCell colSpan={3} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>EQUITY</TableCell></TableRow>
                                {renderRows(data.equity || [])}
                                <TableRow>
                                    <TableCell>Current period net income</TableCell>
                                    <TableCell align="right">{formatCurrency(data.current_period_net_income)}</TableCell>
                                    {format === 'COMMON_SIZE' && <TableCell align="right" />}
                                </TableRow>
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Total Equity</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_equity)}</TableCell>
                                    {format === 'COMMON_SIZE' && <TableCell align="right" />}
                                </TableRow>
                                <TableRow sx={{ '& td': { borderTop: '3px double #111827', fontWeight: 700 } }}>
                                    <TableCell>Total Liabilities + Equity</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_liabilities_and_equity)}</TableCell>
                                    {format === 'COMMON_SIZE' && <TableCell align="right" />}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Chip
                            label={data.balanced ? 'Balanced ✓' : 'Out of balance!'}
                            color={data.balanced ? 'success' : 'error'}
                        />
                    </Stack>
                </Paper>
            )}
        </Box>
    );
}
